/* Whether scheduled work on this deployment survives a restart. The Platform screen's card.
 *
 * **Four states, and collapsing any pair sends an operator to the wrong place.** `production` means
 * a dispatched occurrence outlives the process that accepted it. `stub` means the work still runs —
 * in process, nothing lost — and the in-flight timer does not survive a restart, so reporting it as
 * "scheduling is unavailable" would have somebody hunting a broken feature that is working.
 * `missing` means the runtime is configured and not answering. `unknown` means this screen could
 * not ask, which must not render as a green tick.
 *
 * Reads the readiness response the screen already has rather than issuing its own, and derives
 * `production` from a capability's **absence** from the degraded list — which is that endpoint's
 * shape and the one thing about it worth stating here.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { StatusBadge, type BadgeTone } from '@/shared/ui/StatusBadge';

import { schedulerHealth, type SchedulerFidelity } from '../model/presentation';

/* Fidelity -> tone. Total over the four, so a fifth is a compile error rather than a value
   inheriting whichever tone a `default` happened to carry — the defect §9 records on
   `core.ai.evals`' verdict map, where an unrecognised value took the *permitted* tone.

   `unknown` is `unknown`, this kit's hue for "we could not tell", and deliberately not `ok`. */
const FIDELITY_TONE = {
  production: 'ok',
  stub: 'warn',
  missing: 'danger',
  unknown: 'unknown',
} as const satisfies Record<SchedulerFidelity, BadgeTone>;

export interface SchedulingStatusProps {
  /** The readiness response's degraded list, or `null` when it could not be read. */
  readonly degraded:
    | readonly {
        readonly capability: string;
        readonly fidelity: string;
        readonly detail: string;
      }[]
    | null;
  readonly title: string;
}

export function SchedulingStatus({
  degraded,
  title,
}: SchedulingStatusProps): ReactElement {
  const { t } = useTranslation();
  const health = schedulerHealth(degraded);

  return (
    <Card title={title} hint={t('scheduling.platform.caption')}>
      <DataTable
        caption={t('scheduling.platform.caption')}
        columns={[
          { key: 'capability', label: t('scheduling.platform.capability') },
          { key: 'fidelity', label: t('scheduling.platform.fidelity') },
          { key: 'detail', label: t('scheduling.platform.detail') },
        ]}
      >
        {health.map((entry) => (
          <tr key={entry.capability}>
            <td>
              {entry.capability === 'harvest_scheduler'
                ? t('scheduling.platform.harvest')
                : t('scheduling.platform.execution')}
            </td>
            <td>
              <StatusBadge
                shape="square"
                tone={FIDELITY_TONE[entry.fidelity]}
                label={t(`scheduling.platform.${entry.fidelity}`)}
              />
            </td>
            {/* **The server's own sentence, verbatim.** It names the address, the worker command or
                the variable to set, and a rewritten summary would drop exactly the part an operator
                needs. Production has no detail because there is nothing to do. */}
            <td className="lq-muted">
              {entry.fidelity === 'unknown'
                ? t('scheduling.platform.unknown')
                : entry.detail === ''
                  ? '—'
                  : entry.detail}
            </td>
          </tr>
        ))}
      </DataTable>
    </Card>
  );
}
